import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  RefreshCw, Download, Calendar, Search, ShieldCheck, Upload,
  ArrowDownRight, ArrowUpRight, CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react'

import { api, ApiError } from '@/lib/api/client'
import type {
  ReconciliationResponse, ReconciliationRow, ReconciliationStatus,
} from '@/types/api'
import { ImportDeliveriesModal } from './ImportDeliveriesModal'

const TAB_ORDER: { key: ReconciliationStatus; label: string; tone: ToneKey; icon: typeof CheckCircle2 }[] = [
  { key: 'BILLED_NOT_DELIVERED', label: 'Billed not delivered', tone: 'danger', icon: XCircle        },
  { key: 'DELIVERED_NOT_BILLED', label: 'Delivered not billed', tone: 'warn',   icon: AlertTriangle },
  { key: 'IN_SYNC',              label: 'In sync',              tone: 'ok',     icon: CheckCircle2  },
]
const ACK_TAB = { key: 'ACK',    label: 'Acknowledged',         tone: 'neutral' as ToneKey, icon: ShieldCheck }

type ToneKey = 'ok' | 'warn' | 'danger' | 'neutral'
type TabKey  = ReconciliationStatus | 'ACK'

const TONE_COLOR: Record<ToneKey, string> = {
  ok:      'var(--ns-ok)',
  warn:    'var(--ns-warn)',
  danger:  'var(--ns-err)',
  neutral: 'var(--ns-text-muted)',
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SubscriptionReconciliationPage() {
  const qc = useQueryClient()
  const [tab, setTab]         = useState<TabKey>('BILLED_NOT_DELIVERED')
  const [search, setSearch]   = useState('')
  const [ackTarget, setAckTarget]               = useState<ReconciliationRow | null>(null)
  const [showImport, setShowImport]             = useState(false)

  const { data, isLoading, refetch, isRefetching } = useQuery<ReconciliationResponse>({
    queryKey: ['reconciliation'],
    queryFn:  () => api.get('/reconciliation'),
  })

  const summary = data?.summary ?? {}
  const allRows = data?.rows ?? []

  const counts = {
    BILLED_NOT_DELIVERED: summary['BILLED_NOT_DELIVERED']?.rowCount ?? 0,
    DELIVERED_NOT_BILLED: summary['DELIVERED_NOT_BILLED']?.rowCount ?? 0,
    IN_SYNC:              summary['IN_SYNC']?.rowCount              ?? 0,
    ACK:                  allRows.filter(r => r.acknowledgmentId != null).length,
  }

  const filteredRows = useMemo(() => {
    let rows = allRows
    if (tab === 'ACK') rows = rows.filter(r => r.acknowledgmentId != null)
    else               rows = rows.filter(r => r.status === tab && r.acknowledgmentId == null)
    if (search) {
      const q = search.toLowerCase()
      rows = rows.filter(r =>
        (r.customerName        ?? '').toLowerCase().includes(q) ||
        (r.analyzedCompanyName ?? '').toLowerCase().includes(q),
      )
    }
    return rows
  }, [allRows, tab, search])

  const totalRows = counts.BILLED_NOT_DELIVERED + counts.DELIVERED_NOT_BILLED + counts.IN_SYNC + counts.ACK

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Operations</div>
          <h1 className="page-title">Subscription reconciliation</h1>
          <div className="page-subtitle" style={{ maxWidth: 720 }}>
            Forge subscription lines versus Clementine operational delivery. The single strongest control
            against revenue leakage — review mismatches, acknowledge intentional ones, and add or end lines
            where reality and billing diverge.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw size={13} className={isRefetching ? 'spin' : undefined} /> Refresh
          </button>
          <button className="btn" onClick={() => setShowImport(true)}>
            <Upload size={13} /> Import deliveries
          </button>
          <button className="btn"><Download size={13} /> Export CSV</button>
        </div>
      </div>

      {/* Headline cards */}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <ReconCard
          tone="ok"
          icon={CheckCircle2}
          label="In sync"
          value={counts.IN_SYNC}
          sub={`${summary['IN_SYNC']?.customerCount ?? 0} customers · lines match delivery exactly`}
          impact="Billed = Delivered · 0 issues"
          onClick={() => setTab('IN_SYNC')}
          active={tab === 'IN_SYNC'}
        />
        <ReconCard
          tone="danger"
          icon={XCircle}
          label="Billed not delivered"
          value={counts.BILLED_NOT_DELIVERED}
          sub="Subscription lines without operational data — possible refund"
          impact={<><ArrowDownRight size={11} /> Revenue at risk</>}
          onClick={() => setTab('BILLED_NOT_DELIVERED')}
          active={tab === 'BILLED_NOT_DELIVERED'}
        />
        <ReconCard
          tone="warn"
          icon={AlertTriangle}
          label="Delivered not billed"
          value={counts.DELIVERED_NOT_BILLED}
          sub="Analyzed companies without a subscription — revenue leak"
          impact={<><ArrowUpRight size={11} /> Unbilled revenue</>}
          onClick={() => setTab('DELIVERED_NOT_BILLED')}
          active={tab === 'DELIVERED_NOT_BILLED'}
        />
        <ReconCard
          tone="neutral"
          icon={ShieldCheck}
          label="Acknowledged"
          value={counts.ACK}
          sub="Intentional mismatches (POCs, free trials) — hidden from exception views"
          impact="Sticky across refreshes"
          onClick={() => setTab('ACK')}
          active={tab === 'ACK'}
        />
      </div>

      {/* Stacked recon band */}
      {totalRows > 0 && (
        <div className="card" style={{ padding: 14, marginBottom: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Today
          </div>
          <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
            <ReconBarSegment value={counts.IN_SYNC} total={totalRows} color={TONE_COLOR.ok}      label="In sync" />
            <ReconBarSegment value={counts.BILLED_NOT_DELIVERED} total={totalRows} color={TONE_COLOR.danger} label="BND" />
            <ReconBarSegment value={counts.DELIVERED_NOT_BILLED} total={totalRows} color={TONE_COLOR.warn}   label="DNB" />
            <ReconBarSegment value={counts.ACK} total={totalRows} color={TONE_COLOR.neutral} label="Ack" />
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 8, textAlign: 'right' }}>
            {totalRows} reconciliation rows
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {TAB_ORDER.map(t => (
          <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: TONE_COLOR[t.tone] }} />
              {t.label}
            </span>
            <span className="count" style={{ marginLeft: 6 }}>{counts[t.key]}</span>
          </button>
        ))}
        <button className={`tab ${tab === 'ACK' ? 'active' : ''}`} onClick={() => setTab('ACK')}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={12} /> Acknowledged
          </span>
          <span className="count" style={{ marginLeft: 6 }}>{counts.ACK}</span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ position: 'relative', minWidth: 280 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)' }} />
          <input
            className="input"
            placeholder="Search customer or analyzed company…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 30 }}
          />
        </div>
      </div>

      {/* Rows */}
      {isLoading ? (
        <div className="card" style={{ padding: 36, textAlign: 'center' }}>
          <span className="muted">Loading…</span>
        </div>
      ) : tab === 'IN_SYNC' ? (
        <InSyncEmpty count={counts.IN_SYNC} />
      ) : (
        <ReconciliationTable rows={filteredRows} tab={tab} onAcknowledge={r => setAckTarget(r)} />
      )}

      {ackTarget && (
        <AcknowledgeModal
          row={ackTarget}
          onClose={() => setAckTarget(null)}
          onSaved={() => {
            setAckTarget(null)
            qc.invalidateQueries({ queryKey: ['reconciliation'] })
          }}
        />
      )}

      {showImport && (
        <ImportDeliveriesModal onClose={() => setShowImport(false)} />
      )}
    </div>
  )
}


// ── Sub-components ───────────────────────────────────────────────────────────


function ReconCard({
  tone, icon: Icon, label, value, sub, impact, onClick, active,
}: {
  tone: ToneKey
  icon: typeof CheckCircle2
  label: string
  value: number | string
  sub: string
  impact: React.ReactNode
  onClick: () => void
  active: boolean
}) {
  const color = TONE_COLOR[tone]
  return (
    <button
      className="stat"
      onClick={onClick}
      style={{
        borderLeft: `3px solid ${color}`,
        cursor: 'pointer',
        textAlign: 'left',
        outline: active ? `2px solid color-mix(in oklch, ${color} 50%, transparent)` : 'none',
        outlineOffset: -2,
      }}
    >
      <div className="stat-label" style={{ color }}>
        <Icon size={13} /> {label}
      </div>
      <div className="stat-value tabular" style={{ color }}>{value}</div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.35 }}>{sub}</div>
      <div style={{ fontSize: 11.5, marginTop: 6, color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
        {impact}
      </div>
    </button>
  )
}


function ReconBarSegment({ value, total, color, label }: { value: number; total: number; color: string; label: string }) {
  if (value <= 0) return null
  const pct = (value / total) * 100
  return (
    <div
      title={`${label}: ${value} (${pct.toFixed(1)}%)`}
      style={{
        width: `${pct}%`,
        background: color,
        color: 'white',
        fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 24,
      }}
    >
      {value}
    </div>
  )
}


function ReconciliationTable({
  rows, tab, onAcknowledge,
}: {
  rows: ReconciliationRow[]
  tab: TabKey
  onAcknowledge: (r: ReconciliationRow) => void
}) {
  if (rows.length === 0) {
    return (
      <div className="card" style={{ padding: 36, textAlign: 'center' }}>
        <CheckCircle2 size={26} className="muted" style={{ margin: '0 auto 8px' }} />
        <div className="muted">No rows in this category.</div>
      </div>
    )
  }
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Analyzed company</th>
            <th>Product type</th>
            <th>Status</th>
            {tab === 'ACK' && <>
              <th>Reason</th>
              <th>Acknowledged by</th>
              <th>Sticky until</th>
            </>}
            {tab !== 'ACK' && <>
              <th style={{ textAlign: 'right' }}>Subscription line</th>
              <th style={{ width: 200 }}></th>
            </>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.customerId}-${r.analyzedCompanyId}-${r.productArticleId}-${i}`}>
              <td>{r.customerName ?? '—'}</td>
              <td>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600 }}>{r.analyzedCompanyName ?? '—'}</span>
                  <span className="mono muted" style={{ fontSize: 11 }}>{r.analyzedCompanyExternalId}</span>
                </div>
              </td>
              <td>{r.productType ? <span className="badge" style={{ background: 'color-mix(in oklch, var(--ns-brand-strong) 14%, transparent)', color: 'var(--ns-brand-strong)' }}>{r.productType}</span> : '—'}</td>
              <td><ReconStatusBadge status={r.status} /></td>

              {tab === 'ACK' && <>
                <td className="muted" style={{ fontStyle: 'italic', fontSize: 12.5, maxWidth: 320 }}>{r.acknowledgmentReason ?? '—'}</td>
                <td className="muted" style={{ fontSize: 12 }}>{r.acknowledgedByName ?? '—'}</td>
                <td className="tabular" style={{ fontSize: 12 }}>{r.acknowledgmentValidUntil ?? 'Permanent'}</td>
              </>}

              {tab !== 'ACK' && <>
                <td className="tabular muted" style={{ textAlign: 'right', fontSize: 12 }}>
                  {r.subscriptionLineId != null ? `#${r.subscriptionLineId}` : '—'}
                </td>
                <td>
                  <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm">Investigate</button>
                    <button className="btn btn-sm btn-brand" onClick={() => onAcknowledge(r)}>Acknowledge</button>
                  </div>
                </td>
              </>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}


function ReconStatusBadge({ status }: { status: ReconciliationStatus | string }) {
  const map: Record<string, { color: string; label: string }> = {
    IN_SYNC:              { color: 'var(--ns-ok)',    label: 'In sync'   },
    BILLED_NOT_DELIVERED: { color: 'var(--ns-err)',   label: 'Billed not delivered'   },
    DELIVERED_NOT_BILLED: { color: 'var(--ns-warn)',  label: 'Delivered not billed'   },
  }
  const m = map[status] ?? { color: 'var(--ns-text-muted)', label: status }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: m.color }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: m.color }} /> {m.label}
    </span>
  )
}


function InSyncEmpty({ count }: { count: number }) {
  return (
    <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
      <div style={{
        width: 56, height: 56, borderRadius: 999,
        background: 'color-mix(in oklch, var(--ns-ok) 16%, transparent)',
        color: 'var(--ns-ok)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 12,
      }}>
        <CheckCircle2 size={28} />
      </div>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{count} lines in sync</h3>
      <div className="muted" style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.5 }}>
        Forge subscription lines and Clementine operational delivery agree across all customers.
        Reviewing the matching set is rarely needed — load it on demand if you want to spot-check.
      </div>
    </div>
  )
}


function AcknowledgeModal({
  row, onClose, onSaved,
}: { row: ReconciliationRow; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason]   = useState('Free POC / evaluation')
  const [validUntil, setValidUntil] = useState('')
  const [permanent, setPermanent]   = useState(true)
  const [notes, setNotes]     = useState('')

  const mutation = useMutation({
    mutationFn: async () => api.post('/reconciliation/acknowledge', {
      customerId:         row.customerId,
      analyzedCompanyId:  row.analyzedCompanyId,
      productArticleId:   row.productArticleId,
      mismatchType:       row.status,
      reason:             [reason, notes].filter(Boolean).join(' · '),
      validUntil:         permanent ? null : (validUntil || null),
    }),
    onSuccess: () => { toast.success('Mismatch acknowledged'); onSaved() },
    onError:   (e) => toast.error(e instanceof ApiError ? e.message : 'Could not acknowledge'),
  })

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'color-mix(in oklch, var(--ns-text) 30%, transparent)',
      backdropFilter: 'blur(2px)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth: 540, width: '100%', padding: 22 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Acknowledge mismatch</div>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
          {row.customerName} · {row.analyzedCompanyName} · {row.productType ?? row.articleName}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>Reason</span>
            <select className="input" value={reason} onChange={e => setReason(e.target.value)}>
              <option>Free POC / evaluation</option>
              <option>Internal demo / test environment</option>
              <option>Awaiting customer signature</option>
              <option>Delivery error · operational issue</option>
              <option>Other</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>Sticky until</span>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <input
                type="date"
                className="input"
                value={validUntil}
                disabled={permanent}
                onChange={e => setValidUntil(e.target.value)}
                style={{ flex: 1 }}
              />
              <label className="muted" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={permanent} onChange={e => setPermanent(e.target.checked)} />
                Permanent
              </label>
            </div>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>Notes</span>
            <textarea
              className="input"
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </label>
        </div>

        <div className="card" style={{ marginTop: 14, padding: 12, background: 'var(--ns-surface-2)' }}>
          <div className="muted" style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <Calendar size={12} style={{ marginTop: 2 }} />
            <span>
              Acknowledgments hide this row from the exception views and stick across refreshes.
              If you set a sticky date, the row re-surfaces automatically when that date passes.
            </span>
          </div>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-brand" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Acknowledge'}
          </button>
        </div>
      </div>
    </div>
  )
}
